import { useSyncStatus } from '../hooks/useAccountBalances';
import { Check, AlertCircle } from 'lucide-react';

/**
 * Subtle, premium sync-status pill for the Dashboard header.
 * Reflects the lifecycle of optimistic balance mutations driven by the
 * shared cache. Hidden when idle.
 *
 *   syncing → pulsing amber dot + "Syncing…"
 *   synced  → green dot + check + "Synced"        (auto-hides after 1.5s)
 *   error   → red dot + alert + "Sync failed"     (auto-hides after 3s)
 */
function SyncStatusPill() {
  const { status, label } = useSyncStatus();

  if (status === 'idle') return null;

  const variants = {
    syncing: {
      dot: 'bg-amber-400',
      ring: 'ring-amber-400/30',
      text: 'text-amber-200',
      bg: 'bg-amber-500/10 border-amber-500/30',
      pulse: true,
      icon: null,
      label: label || 'Syncing…',
    },
    synced: {
      dot: 'bg-emerald-400',
      ring: 'ring-emerald-400/30',
      text: 'text-emerald-200',
      bg: 'bg-emerald-500/10 border-emerald-500/30',
      pulse: false,
      icon: Check,
      label: 'Synced',
    },
    error: {
      dot: 'bg-rose-400',
      ring: 'ring-rose-400/30',
      text: 'text-rose-200',
      bg: 'bg-rose-500/10 border-rose-500/30',
      pulse: false,
      icon: AlertCircle,
      label: 'Sync failed',
    },
  };

  const v = variants[status];
  const Icon = v.icon;

  return (
    <div
      data-testid="sync-status-pill"
      data-sync-status={status}
      role="status"
      aria-live="polite"
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium backdrop-blur-sm transition-all duration-200 ${v.bg} ${v.text}`}
    >
      <span className="relative inline-flex items-center justify-center">
        <span
          className={`inline-block w-2 h-2 rounded-full ${v.dot} ring-2 ${v.ring}`}
        />
        {v.pulse && (
          <span
            className={`absolute inline-block w-2 h-2 rounded-full ${v.dot} opacity-75 animate-ping`}
            aria-hidden="true"
          />
        )}
      </span>
      {Icon && <Icon className="w-3 h-3" aria-hidden="true" />}
      <span>{v.label}</span>
    </div>
  );
}

export default SyncStatusPill;
